import { supabase, q } from '../lib/supabase.js';
import * as Demo from '../demo/demo.js';

/**
 * Tarefas, Pesquisas, Base de Conhecimento e Realizado do Dia.
 *
 * Atenção: no protótipo as três primeiras eram VITRINE — arrays fixos
 * (TAREFAS, PESQUISAS, KB) que não salvavam nada. Só o Realizado do Dia
 * persistia, em localStorage e apenas do dia corrente. Aqui tudo grava.
 */

/* ---------------------------------------------------------------- */
/* Tarefas                                                          */
/* ---------------------------------------------------------------- */

const TAREFA_CAMPOS = `
  id, titulo, prazo, prioridade, status, criado_em, criado_por,
  empresa:empresas ( id, nome ),
  responsavel:colaboradores!tarefas_responsavel_id_fkey ( id, nome )
`;

export async function listarTarefas({ status = null, responsavelId = null } = {}) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.tarefas.filter((t) => (!status || t.status === status) && (!responsavelId || t.responsavel?.id === responsavelId));
  }

  // Limite defensivo: com 170 empresas a lista cresce rápido e nenhuma tela
  // precisa de tudo de uma vez.
  let sel = supabase
    .from('tarefas')
    .select(TAREFA_CAMPOS)
    .order('prazo', { ascending: true, nullsFirst: false })
    .limit(500);

  if (status) sel = sel.eq('status', status);
  if (responsavelId) sel = sel.eq('responsavel_id', responsavelId);

  return q(sel);
}

export async function criarTarefa(dados, colaboradorId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const nova = {
      id: 't-demo-' + Date.now(),
      titulo: dados.titulo, prazo: dados.prazo || null,
      prioridade: dados.prioridade || 'Média', status: dados.status || 'Pendente',
      empresa: Demo.empresas.filter((e) => e.id === dados.empresa_id).map((e) => ({ id: e.id, nome: e.nome }))[0] || null,
      responsavel: Demo.colaboradores.find((c) => c.id === dados.responsavel_id) || null,
    };
    Demo.tarefas.unshift(nova);
    return nova;
  }

  const [linha] = await q(
    supabase.from('tarefas').insert({ ...dados, criado_por: colaboradorId }).select(TAREFA_CAMPOS)
  );
  return linha;
}

export async function atualizarTarefa(id, campos) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const t = Demo.tarefas.find((x) => x.id === id);
    if (t) {
      Object.assign(t, campos);
      if (campos.empresa_id) t.empresa = Demo.empresas.find((e) => e.id === campos.empresa_id);
      if (campos.responsavel_id) t.responsavel = Demo.colaboradores.find((c) => c.id === campos.responsavel_id);
    }
    return t;
  }

  const [linha] = await q(
    supabase.from('tarefas').update(campos).eq('id', id).select(TAREFA_CAMPOS)
  );
  return linha;
}

export async function removerTarefa(id) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const i = Demo.tarefas.findIndex((x) => x.id === id);
    if (i >= 0) Demo.tarefas.splice(i, 1);
    return;
  }

  await q(supabase.from('tarefas').delete().eq('id', id));
}

/* ---------------------------------------------------------------- */
/* Pesquisas                                                        */
/* ---------------------------------------------------------------- */

const PESQUISA_CAMPOS = `
  id, assunto, conclusao, fonte, data,
  empresa:empresas ( id, nome ),
  autor:colaboradores ( id, nome )
`;

export async function listarPesquisas(busca = '') {
  if (Demo.emDemonstracao) return Demo.pesquisas;   // guarda do modo demonstração

  let sel = supabase.from('pesquisas').select(PESQUISA_CAMPOS).order('data', { ascending: false });

  if (busca.trim()) {
    const termo = `%${busca.trim()}%`;
    sel = sel.or(`assunto.ilike.${termo},conclusao.ilike.${termo},fonte.ilike.${termo}`);
  }

  return q(sel);
}

export async function criarPesquisa(dados, colaboradorId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const nova = {
      id: 'p-demo-' + Date.now(),
      assunto: dados.assunto, conclusao: dados.conclusao, fonte: dados.fonte || null,
      data: dados.data || new Date().toISOString().slice(0, 10),
      empresa: Demo.empresas.filter((e) => e.id === dados.empresa_id).map((e) => ({ id: e.id, nome: e.nome }))[0] || null,
      autor: Demo.colaboradores.find((c) => c.id === colaboradorId) || null,
    };
    Demo.pesquisas.unshift(nova);
    return nova;
  }

  const [linha] = await q(
    supabase.from('pesquisas').insert({ ...dados, autor_id: colaboradorId }).select(PESQUISA_CAMPOS)
  );
  return linha;
}

export async function atualizarPesquisa(id, campos) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const p = Demo.pesquisas.find((x) => x.id === id);
    if (p) Object.assign(p, campos);
    return p;
  }

  const [linha] = await q(
    supabase.from('pesquisas').update(campos).eq('id', id).select(PESQUISA_CAMPOS)
  );
  return linha;
}

/* ---------------------------------------------------------------- */
/* Base de conhecimento                                             */
/* ---------------------------------------------------------------- */

export async function baseConhecimento() {
  if (Demo.emDemonstracao) return Demo.kb;   // guarda do modo demonstração

  return q(
    supabase
      .from('kb_categorias')
      .select('id, nome, ordem, artigos:kb_artigos ( id, titulo, conteudo, atualizado_em )')
      .order('ordem')
  );
}

export async function criarArtigo({ categoria_id, titulo, conteudo }, colaboradorId) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const cat = Demo.kb.find((c) => c.id === categoria_id);
    const novo = { id: 'k-demo-' + Date.now(), titulo, conteudo };
    if (cat) cat.artigos.push(novo);
    return novo;
  }

  const [linha] = await q(
    supabase
      .from('kb_artigos')
      .insert({ categoria_id, titulo, conteudo, autor_id: colaboradorId })
      .select()
  );
  return linha;
}

export async function atualizarArtigo(id, campos) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    for (const cat of Demo.kb) {
      const a = (cat.artigos || []).find((x) => x.id === id);
      if (a) { Object.assign(a, campos); return a; }
    }
    return null;
  }

  const [linha] = await q(supabase.from('kb_artigos').update(campos).eq('id', id).select());
  return linha;
}

/* ---------------------------------------------------------------- */
/* Realizado do dia                                                 */
/* ---------------------------------------------------------------- */

const LOG_CAMPOS = `
  id, data, hora, atividade, status, empresa_texto,
  empresa:empresas ( id, nome ),
  colaborador:colaboradores ( id, nome )
`;

function hoje() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * O protótipo só guardava o dia corrente e só no navegador de quem digitou.
 * Agora dá para ver dias anteriores e a gestão enxerga o time inteiro.
 */
export async function listarRealizado({ data = hoje(), colaboradorId = null } = {}) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    return Demo.realizado.filter((l) => l.data === data && (!colaboradorId || l.colaborador?.id === colaboradorId))
      .sort((a, b) => a.hora.localeCompare(b.hora));
  }

  let sel = supabase.from('realizado_dia').select(LOG_CAMPOS).eq('data', data).order('hora');
  if (colaboradorId) sel = sel.eq('colaborador_id', colaboradorId);
  return q(sel);
}

export async function registrarRealizado(
  { hora, empresa_id = null, empresa_texto = null, atividade, status = 'Concluído', data = hoje() },
  colaboradorId
) {
  if (Demo.emDemonstracao) {   // guarda do modo demonstração
    const empresa = Demo.empresas.find((e) => e.id === empresa_id);
    const novo = { id: 'r-demo-' + Date.now(), data, hora, atividade, status,
      empresa: empresa ? { id: empresa.id, nome: empresa.nome } : null,
      empresa_texto, colaborador: Demo.colaboradores.find((c) => c.id === colaboradorId) };
    Demo.realizado.push(novo);
    return novo;
  }

  const [linha] = await q(
    supabase
      .from('realizado_dia')
      .insert({
        colaborador_id: colaboradorId,
        data,
        hora,
        empresa_id,
        empresa_texto,
        atividade,
        status,
      })
      .select(LOG_CAMPOS)
  );
  return linha;
}

export async function removerRealizado(id) {
  await q(supabase.from('realizado_dia').delete().eq('id', id));
}

/**
 * Colaborador sugere correção num manual; a gestão aplica.
 *
 * A edição da base virou exclusiva da gestão a pedido dela — "senão eu
 * vou começar a perder as minhas bases". Este é o outro lado: tirar o
 * poder de editar sem dar um canal de aviso faria quem achasse um erro
 * simplesmente não avisar.
 */
export async function sugerirCorrecao({ artigoId, texto }, colaboradorId) {
  if (Demo.emDemonstracao) return { id: 's-demo-' + Date.now() };   // guarda do modo demonstração

  const [linha] = await q(
    supabase
      .from('kb_solicitacoes')
      .insert({ artigo_id: artigoId, texto, solicitante_id: colaboradorId })
      .select('id')
  );
  return linha;
}

/** As sugestões esperando decisão da gestão. */
export async function sugestoesAbertas() {
  if (Demo.emDemonstracao) return [];   // guarda do modo demonstração
  return q(supabase.from('v_kb_solicitacoes_abertas').select('*'));
}

/* ------------------------------------------------------------------ */
/* O realizado que vem do checklist                                    */
/*                                                                     */
/* Pergunta dela: "a pessoa vai preencher o checklist e vai preencher   */
/* lá de novo?" Não. O que passa pelo checklist aparece sozinho; o      */
/* campo escrito à mão fica para o que não passa — consultoria,         */
/* treinamento, reunião.                                                */
/* ------------------------------------------------------------------ */

export async function realizadoDoDia({ data = hoje(), colaboradorId = null } = {}) {
  if (Demo.emDemonstracao) return realizadoDoDiaDemo(data, colaboradorId);   // guarda do modo demonstração

  const { data: linhas, error } = await supabase.rpc('realizado_do_dia', {
    p_data: data, p_colaborador: colaboradorId,
  });
  if (error) throw new Error(error.message);
  return linhas || [];
}

function realizadoDoDiaDemo(data, colaboradorId) {
  const alvo = colaboradorId || Demo.colaboradores[0]?.id;
  const doDia = Demo.historico.filter(
    (h) => ['item', 'checklist'].includes(h.tipo)
      && h.colaborador?.id === alvo
      && h.criado_em.slice(0, 10) === data
  );

  const acaoDe = (s) =>
    ['ok', 'feito'].includes(s) ? 'concluiu'
    : s === 'parcial' ? 'deixou em andamento'
    : ['pendente', 'não feito'].includes(s) ? 'voltou para a realizar'
    : (s || '—');

  const grupos = new Map();
  for (const h of doDia) {
    const frente = h.tipo === 'item' ? h.descricao : 'Checklist';
    const chave = [h.empresa?.nome, frente, acaoDe(h.status_novo)].join('§');
    const g = grupos.get(chave) || {
      origem: h.tipo === 'item' ? 'frente' : 'checklist',
      empresa: h.empresa?.nome || '—',
      frente,
      detalhe: acaoDe(h.status_novo),
      meses: new Set(), etapas: new Set(), quantidade: 0,
      primeira_hora: h.criado_em.slice(11, 16), ultima_hora: h.criado_em.slice(11, 16),
    };
    g.meses.add(h.competencia);
    g.etapas.add(h.descricao);
    g.quantidade++;
    g.ultima_hora = h.criado_em.slice(11, 16);
    grupos.set(chave, g);
  }

  return [...grupos.values()].map((g) => ({
    ...g,
    detalhe: g.origem === 'checklist' ? `${g.detalhe} ${g.etapas.size} etapa(s)` : g.detalhe,
    competencias: [...g.meses].sort()
      .map((m) => m.slice(5, 7) + '/' + m.slice(0, 4))
      .join(', '),
  })).sort((a, b) => a.primeira_hora.localeCompare(b.primeira_hora));
}
